import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';
import type { Dispatch } from 'redux';
import { useCallback } from 'react';

import { Button } from 'UIPackage/MainModule/components/Button';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { DATE_FORMAT_ISO_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';

import {
  getSmevMonitoringEndpoints,
  getSmevMonitoringMessageTypes,
  getSmevMonitoringProcessingStatuses,
  getSmevMonitoringSenders,
  getSmevMonitoringSendingStatuses,
  getSmevMonitoringServiceTypes,
  getSmevMonitoringSmevMessageTypes,
  getSmevMonitoringSystemTypes,
} from '../selectors/smevMonitoringSelectors';
import styles from './SmevMonitoringFilters.module.scss';
import { ISmevMonitoringFilter } from '../interfaces/ISmevMonitoringFilter';
import {
  smevMonitoringGetTableData,
  smevMonitoringSetFilter,
  smevMonitoringSetFilterForm,
} from '../actions/smevMonitoringActions';
import { makePlanIdStringValidator } from '../common/makePlanIdStringValidator';
import { ISmevMonitoringServiceType } from '../interfaces/ISmevMonitoringServiceType';

interface ISmevMonitoringFiltersProps {
  systemTypesOptions: ISelectOption<string>[];
  serviceTypesAll: ISmevMonitoringServiceType[];
  endpointsSelectOptions: ISelectOption<string>[];
  sendersSelectOptions: ISelectOption<string>[];
  messageTypesSelectOptions: ISelectOption<string>[];
  smevMessageTypesSelectOptions: ISelectOption<string>[];
  processingStatusesSelectOptions: ISelectOption<string>[];
  sendingStatusesSelectOptions: ISelectOption<string>[];
}

const SmevMonitoringFiltersWithoutStore = ({
  systemTypesOptions,
  serviceTypesAll,
  endpointsSelectOptions,
  sendersSelectOptions,
  messageTypesSelectOptions,
  smevMessageTypesSelectOptions,
  processingStatusesSelectOptions,
  sendingStatusesSelectOptions,
}: ISmevMonitoringFiltersProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const handleFormSubmit = useCallback(
    (values: ISmevMonitoringFilter) => {
      dispatch(
        smevMonitoringSetFilter({
          ...values,
          ...(values.receiveCreatedAt && {
            receiveCreatedAt: formatDate({ date: values.receiveCreatedAt, dateFormat: DATE_FORMAT_ISO_FNS }),
          }),
          endpoint: values.endpoint?.value,
          service: values.service?.value,
          system: values.system?.value,
          processingStatus: values.processingStatus?.value,
          sendingStatus: values.sendingStatus?.value,
          monitoringType: values.monitoringType?.value,
          sender: values.sender?.value,
          smevMessageType: values.smevMessageType?.value,
        }),
      );
      dispatch(smevMonitoringGetTableData());
    },
    [dispatch],
  );

  useUnmount(() => {
    dispatch(smevMonitoringSetFilter(null));
  });

  const renderForm = useCallback(
    (props: FormRenderProps<ISmevMonitoringFilter>) => {
      const { handleSubmit, form, values } = props;

      dispatch(smevMonitoringSetFilterForm(form));

      function handleResetForm() {
        form.reset();
        dispatch(smevMonitoringSetFilter(null));
        dispatch(smevMonitoringGetTableData());
      }

      function handleSystemChange() {
        form.change('service');
      }

      const serviceTypeOptions = serviceTypesAll.find(item => item.systemType === values.system?.value)?.options;

      return (
        <form className={styles.Form} onSubmit={handleSubmit}>
          <Row notWrap>
            <FieldBlock label="Номер идентификатора" color="black" margin="right">
              <TextareaField name="messageUUIDString" resize="none" />
            </FieldBlock>
            <FieldBlock label="Номер КНМ" color="black" margin="right">
              <TextInputField name="knmErpId" />
            </FieldBlock>
            <FieldBlock label="Номер плана" color="black" margin="right">
              <TextInputField
                name="planId"
                validate={makePlanIdStringValidator('Номер плана должен содержать 10 цифр')}
              />
            </FieldBlock>
            <FieldBlock label="Эндпоинт" color="black">
              <SelectInputField name="endpoint" options={endpointsSelectOptions} />
            </FieldBlock>
          </Row>
          <Row notWrap>
            <FieldBlock label="Система" color="black" margin="right">
              <SelectInputField name="system" options={systemTypesOptions} onChange={handleSystemChange} />
            </FieldBlock>
            <FieldBlock label="Вид сведений" color="black" margin="right">
              <SelectInputField name="service" options={serviceTypeOptions} />
            </FieldBlock>
            <FieldBlock label="Дата получения" color="black" margin="right">
              <DatePickerField name="receiveCreatedAt" />
            </FieldBlock>
            <FieldBlock label="Статус обработки" color="black">
              <SelectInputField name="processingStatus" options={processingStatusesSelectOptions} />
            </FieldBlock>
          </Row>
          <Row notWrap>
            <FieldBlock label="Статус отправки" color="black" margin="right">
              <SelectInputField name="sendingStatus" options={sendingStatusesSelectOptions} />
            </FieldBlock>
            <FieldBlock label="Тип сообщения" color="black" margin="right">
              <SelectInputField name="monitoringType" options={messageTypesSelectOptions} />
            </FieldBlock>
            <FieldBlock label="Внешняя система" color="black">
              <SelectInputField name="sender" options={sendersSelectOptions} />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Тип статусного сообщения" color="black">
              <Row notWrap notSpace>
                <FieldBlock size="col-4" margin="right">
                  <SelectInputField name="smevMessageType" options={smevMessageTypesSelectOptions} />
                </FieldBlock>
                <FieldBlock size="half">
                  <Button className={styles.SubmitButton} text="Применить" color="primary" type="submit" />
                  <Button text="Cбросить фильтр" color="default" type="reset" onClick={handleResetForm} />
                </FieldBlock>
              </Row>
            </FieldBlock>
          </Row>
        </form>
      );
    },
    [
      dispatch,
      endpointsSelectOptions,
      messageTypesSelectOptions,
      processingStatusesSelectOptions,
      sendersSelectOptions,
      sendingStatusesSelectOptions,
      serviceTypesAll,
      smevMessageTypesSelectOptions,
      systemTypesOptions,
    ],
  );

  return <Form<ISmevMonitoringFilter> render={renderForm} onSubmit={handleFormSubmit} />;
};

const mapStateToProps = (state: IRootState) => ({
  systemTypesOptions: getSmevMonitoringSystemTypes(state),
  serviceTypesAll: getSmevMonitoringServiceTypes(state),
  endpointsSelectOptions: getSmevMonitoringEndpoints(state),
  sendersSelectOptions: getSmevMonitoringSenders(state),
  messageTypesSelectOptions: getSmevMonitoringMessageTypes(state),
  smevMessageTypesSelectOptions: getSmevMonitoringSmevMessageTypes(state),
  processingStatusesSelectOptions: getSmevMonitoringProcessingStatuses(state),
  sendingStatusesSelectOptions: getSmevMonitoringSendingStatuses(state),
});

export const SmevMonitoringFilters = connect(mapStateToProps)(SmevMonitoringFiltersWithoutStore);
